import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {useTranslation} from 'react-i18next';
import {Location} from 'history';
import useHistoryState from 'use-history-state';
import client from 'lib/CommonApi/client';
import {callAlertWeb, callNativeWeb, footerShowEventWeb, hideProgressWeb, showProgressWeb} from 'common/appInterface';
import {AxiosError} from "axios";
import Modal from '../../components/Modal';
import StringUtils from "common/stringUtils";

const CardMonthlyFeeTypeView: React.FC = () => {
    /** Location State */
    const location = useLocation() as Location<LocationState>;
    /** Navigate */
    const navigate = useNavigate();
    /** 다국어처리 */
    const { t, i18n } = useTranslation();

    const screenId = 'SERV17';
    const screenName = t('SERV17.title02', 'Select Card Monthly Fee Type');

    const cardInfo = location.state.info;
    const [onLoad, setOnLoad] = useState<boolean>(false);
    const [monthlyFeeTypeList, setMonthlyFeeTypeList] = useState<Array<CardMonthlyTypeInfo>>([]);
    // 선택한 월회비 유형 — 다음 화면에서 뒤로 돌아왔을 때 유지 (history state 저장)
    const [monthlyFeeType, setMonthlyFeeType] = useHistoryState<string>('01', 'monthlyFeeType');
    const [nextChargeMonth, setNextChargeMonth] = useState<string>('');


    const [isModal, setIsModal] = useState<boolean>(false);

    /** 카드신청 흐름 여부 — 디자인 선택 다음, 거래비밀번호 입력 전 단계 (/apply/step7). 선택값은 거래비밀번호 입력 후 접수 생성 시 저장한다. */
    const isCardApply = location.pathname == '/apply/step7';
    /** 카드신청에서 뒤로 돌아와 이전 선택값이 복원된 경우 — 서버 조회값으로 덮어쓰지 않는다 */
    const [isRestoredFeeType] = useState<boolean>(() => isCardApply && window.history.state?.monthlyFeeType !== undefined);

    const inquiryMonthlyFeeType = useCallback(
        async () => {
            showProgressWeb();
            console.log('cardApply', location.state.apply);

            await client.get('/api/app/apply/inquiryMonthlyFeeType', {
                params : {
                    ...location.state.query
                    ,cardProductCode : isCardApply ? location.state.apply.cardProductCode : cardInfo.cardProductCode
                    ,alternativeCardNo : isCardApply ? '' : cardInfo.alternativeCardNo
                    ,cardMemberNo : isCardApply ? '' : cardInfo.cardMemberNo
                }
            }).then(response => {
                if (response.data.code === "00") {
                    setMonthlyFeeTypeList(response.data.response);

                    if (response.data.response.length > 0 && response.data.response[0].monthlyFeePayTypeCode !== '') {
                        if (!isRestoredFeeType) {
                            setMonthlyFeeType(StringUtils.checkValueNull( response.data.response[0].monthlyFeePayTypeCode)?'01':response.data.response[0].monthlyFeePayTypeCode, true);
                        }
                        setNextChargeMonth(response.data.response[0].nextChargeMonth);
                    }
                }
            }).catch((e: AxiosError) => {
                callAlertWeb('ERROR', '99', e.message);
            }).finally(() => {
                hideProgressWeb();
            });

        },
        [location, cardInfo, isCardApply, isRestoredFeeType, setMonthlyFeeType],
    );

    const onClickMonthlyFeeType = (typeCode:string) => {
        setMonthlyFeeType(typeCode, true);
    };

    const changeMonthlyFeeTypeAPI = useCallback(
        async () => {
            showProgressWeb();

            await client.post('/api/app/apply/changeMonthlyFeeType',{
                 ...location.state.query
                ,cardProductCode : cardInfo.cardProductCode
                ,alternativeCardNo : cardInfo.alternativeCardNo
                ,cardMemberNo : cardInfo.cardMemberNo
                ,monthlyFeePayTypeCode: monthlyFeeType
                ,processStep : 'monthlyFeeTypeChange'
                ,receiveSeq : ''
                ,receiveDate : ''
            }).then(response => {
                console.log('changeMonthlyFeeType response', response);
                if (response.data.code === "00") {
                    callAlertWeb("SUCCESS", response.data.code, response.data.message);

                    navigate("/main", {
                        state: {
                            query: location.state.query
                            ,swiperIndex: location.state.swiperIndex
                            ,tabIndex: location.state.tabIndex
                            ,from: location.pathname
                        }
                    });
                } else if (response.data.code === '98') {
                    setIsModal(true);
                }
            }).catch((e: AxiosError) => {
                callAlertWeb('ERROR', '99', e.message);
            }).finally(() => {
                hideProgressWeb();
            });

        },
        [location, cardInfo, monthlyFeeType, navigate],
    );

    /** Next 클릭 — 카드신청은 선택값을 가지고 거래비밀번호 입력으로 이동 (접수 전이라 여기서 저장하지 않음) */
    const clickNext = useCallback(() => {
        if (isCardApply) {
            navigate("/apply/step2", { state: { ...location.state, apply: { ...location.state.apply, monthlyFeePayTypeCode: monthlyFeeType }}});
        } else {
            changeMonthlyFeeTypeAPI();
        }
    }, [isCardApply, navigate, location.state, monthlyFeeType, changeMonthlyFeeTypeAPI]);

    const closeModal = useCallback(
        () => {
            navigate("/main", {
                state: {
                    query: location.state.query
                    ,swiperIndex: location.state.swiperIndex
                    ,tabIndex: location.state.tabIndex
                    ,from: location.pathname
                    ,linkOpenMenu : 'monthlyFeeHistory'
                }});
        },
        [navigate, location],
    );

    /** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) — 백키/하드웨어 back 공통. 카드신청은 디자인 선택으로 이동 */
    const callbackBackkeyEvent = useCallback(() => {
        if (isCardApply) {
            navigate(-1);
            return;
        }
        if (location.state.linkOpenMenu === 'main') {
            navigate("/main", {
                state: {
                    query: location.state.query
                    ,swiperIndex: location.state.swiperIndex
                    ,tabIndex: location.state.tabIndex
                    ,from: location.pathname
                }
            });
        } else {
            navigate(-1);
        }
    }, [isCardApply, navigate, location.state.linkOpenMenu, location.state.query, location.state.swiperIndex, location.state.tabIndex, location.pathname]);

    // 최초실행
    useEffect(() => {
        // 언어설정
        i18n.changeLanguage(location.state.query.languageCode);
        // callNative_Web 처리 (WEB > APP)
        // 카드신청은 메인 화면이 아님 (신청 흐름에서도 from 이 '/intro' 로 남아 있어 mainYn 'Y' 가 되면 앱이 뒤로가기 시 WebView 를 닫음)
        callNativeWeb(screenId, screenName, !isCardApply && location.state.from === '/intro' ? 'Y' : 'N');
        // 하단바 숨기기
        footerShowEventWeb('N');

        console.log('QUERY STATE');
        console.log(location.state);
        console.log(cardInfo);

        // 최초한번실행 useEffect() Warning 제거
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
    useEffect(() => {
        document.addEventListener('backpress', callbackBackkeyEvent);
        return () => document.removeEventListener('backpress', callbackBackkeyEvent);
    }, [callbackBackkeyEvent]);

    useEffect(() => {
        if (!onLoad) {
            // 카드 월회비 선입금
            inquiryMonthlyFeeType();
            setOnLoad(true);
        }
    },[location, inquiryMonthlyFeeType, onLoad, setOnLoad]);

    return (
        <section className="pdb70">
            <div id='wrap'>
                <div id='container' className='pcHidden'>
                    <section className='monthlyFeeType'>
                        <p className='heading'>
                            {t('SERV17.explan06', 'Choose how to pay your monthly card fee')}
                        </p>
                        <div className='feeList'>
                            {
                                (monthlyFeeTypeList !== null && monthlyFeeTypeList.length > 0) && (
                                    monthlyFeeTypeList.map((item:CardMonthlyTypeInfo, index:number) => (
                                        item.typeCode === '01' ? (
                                            <div key={index} className={item.typeCode === monthlyFeeType?'feeOption on':'feeOption'} onClick={()=>onClickMonthlyFeeType(item.typeCode)}>
                                                <p className='feeTitle'>{item.typeTitle}</p>
                                                <p className='price'>{item.payFee} KRW</p>
                                            </div>
                                        ) : (
                                            <div key={index} className={item.typeCode === monthlyFeeType?'feeOption on':'feeOption'} onClick={()=>onClickMonthlyFeeType(item.typeCode)}>
                                                <div className='feeRow'>
                                                    <div className='feeLeft'>
                                                        <p className='feeTitle'>{item.typeTitle}</p>
                                                        <span className='discountTag'>{t('SERV17.badge01', '{{N}}% off', { 'N': item.discountRate })}</span>
                                                    </div>
                                                    <div className='feeRight'>
                                                        <p className='originalPrice'>{item.totalFee} KRW</p>
                                                        <p className='price'>{item.payFee} KRW</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    ))
                                )
                            }
                        </div>
                        <div className='detailBox'>
                            <p className='detailTitle'>{t('SERV17.header04')}</p>
                            <ul className='detailList'>
                                {
                                    !isCardApply && (
                                        <li>{t('SERV17.explan02')}<br/>&#40;<span className='txtEffect02'>{nextChargeMonth}</span>&#41;</li>
                                    )
                                }
                                <li>{t('SERV17.explan04')}</li>
                                <li>{t('SERV17.explan05')}</li>
                            </ul>
                        </div>
                    </section>
                    <div className="btnArea">
                        <div className="btnWrapTy01">
                            <button type="button" className="btnPrimaryTy01" onClick={clickNext}><span>{t("COMMON.button.next")}</span></button>
                        </div>
                    </div>
                </div>
            </div>
            <Modal isOpen={isModal} contentLabel="modal" onRequestClose={closeModal} className="popTyLayer2" overlayClassName="dim" closeTimeoutMS={500}>
                <p className="message">{t("SERV17.modal.content")}</p>
                <div className="btnWrapTy02">
                    <button type="button" className="btnPrimaryTy03" onClick={() => closeModal()}><span>{t("SERV17.modal.button")}</span></button>
                </div>
            </Modal>
        </section>
    );
};

export default CardMonthlyFeeTypeView;